/**
 * Module: screens/onboarding/CoachmarksOverlay
 * Role: One-time 3-step spotlight tour on the new buyer's empty home (FTR-1114).
 *       Hand-rolled (no library): fixed-viewport overlay (z=200, above TabBar z=30),
 *       4-div dim-with-cutout + highlight ring + tooltip card. Measures real DOM via
 *       getBoundingClientRect (viewport coords — no data-coach-stage needed).
 *
 *       Robustness (overrides design's black-overlay trap):
 *         - missing / 0-size target → auto-advance to next tip; all missing → onDone().
 *         - useLayoutEffect for first measure (no flash, L-407).
 *         - ResizeObserver + window resize/scroll → re-measure.
 *         - TG BackButton during tour → onDone() (L-042), stays on home.
 *
 * ADR-023: platform access only via usePlatform() — no window.Telegram, no platform literals.
 *
 * Uses:
 *   - providers/PlatformProvider:usePlatform (back button, haptics)
 *   - screens/onboarding/coachmarks:TIPS, COACH_TARGETS
 *   - screens/flow/atoms/Caveat
 *   - styles/tokens:P, T
 *
 * Used by: screens/HomeScreen/index (rendered conditionally on first-launch nav state)
 * Glossary: ai/glossary/buyer.md
 */
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { usePlatform } from '../../providers/PlatformProvider';
import { Caveat } from '../flow/atoms/Caveat';
import { P, T } from '../../styles/tokens';
import { TIPS } from './coachmarks';

interface CoachmarksOverlayProps {
  /** Called on finish / skip / back / all-targets-missing. Parent unmounts the overlay. */
  onDone: () => void;
}

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PAD = 6;
const DIM = 'rgba(20,16,10,0.62)';
const TIP_EST_HEIGHT = 168;

export function CoachmarksOverlay({ onDone }: CoachmarksOverlayProps) {
  const platform = usePlatform();
  const [step, setStep] = useState(0);
  const [box, setBox] = useState<Box | null>(null);

  const tip = TIPS[step];

  // ─── Measure current target (viewport coords). Auto-advance on missing/0-size. ──
  const measure = useCallback(() => {
    const current = TIPS[step];
    if (!current) {
      onDone();
      return;
    }
    const el = document.querySelector(`[data-coach="${current.target}"]`);
    const rect = el?.getBoundingClientRect();
    const valid = !!rect && rect.width > 0 && rect.height > 0;

    if (!valid) {
      // Override design's "no box → full black dim" trap: skip to the next tip,
      // and if this was the last one, close the tour entirely.
      setBox(null);
      if (step + 1 >= TIPS.length) onDone();
      else setStep((s) => s + 1);
      return;
    }

    setBox({
      top: rect.top - PAD,
      left: rect.left - PAD,
      width: rect.width + PAD * 2,
      height: rect.height + PAD * 2,
    });
  }, [step, onDone]);

  // First measure before paint (no flicker — L-407).
  useLayoutEffect(() => {
    measure();
  }, [measure]);

  // Re-measure on viewport changes + target resize.
  useEffect(() => {
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    let ro: ResizeObserver | undefined;
    const current = TIPS[step];
    const el = current ? document.querySelector(`[data-coach="${current.target}"]`) : null;
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure());
      ro.observe(el);
    }
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      ro?.disconnect();
    };
  }, [measure, step]);

  // TG BackButton intercept (L-042): close tour, stay on home.
  useEffect(() => {
    platform.showBackButton(onDone);
    return () => platform.hideBackButton();
  }, [platform, onDone]);

  // Light haptic when a tip lands.
  useEffect(() => {
    platform.hapticImpact('light');
  }, [platform, step]);

  if (!tip || !box) return null;

  const last = step === TIPS.length - 1;

  // Auto-flip: honour tip.tipBelow when there is room, otherwise flip to the other side.
  const placeBelow = tip.tipBelow
    ? box.top + box.height + 14 + TIP_EST_HEIGHT <= window.innerHeight
    : box.top - 14 - TIP_EST_HEIGHT < 0;
  const tipTop = placeBelow ? box.top + box.height + 14 : Math.max(16, box.top - 14);
  const tipTransform = placeBelow ? 'none' : 'translateY(-100%)';

  return (
    <div
      role="dialog"
      aria-label="Знакомство с приложением"
      style={{ position: 'fixed', inset: 0, zIndex: 200, animation: 'fadeIn 0.25s ease' }}
    >
      {/* Dim with rectangular cutout (4 divs — box-shadow rejected on iOS WKWebView) */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: box.top, background: DIM }} />
      <div style={{ position: 'absolute', top: box.top + box.height, left: 0, right: 0, bottom: 0, background: DIM }} />
      <div style={{ position: 'absolute', top: box.top, height: box.height, left: 0, width: box.left, background: DIM }} />
      <div style={{ position: 'absolute', top: box.top, height: box.height, left: box.left + box.width, right: 0, background: DIM }} />

      {/* Highlight ring */}
      <div
        style={{
          position: 'absolute',
          top: box.top,
          left: box.left,
          width: box.width,
          height: box.height,
          borderRadius: tip.radius,
          border: `2px solid ${P.terracotta}`,
          boxShadow: '0 0 0 4px rgba(226,114,91,0.30)',
          pointerEvents: 'none',
          boxSizing: 'border-box',
          animation: 'fadeIn 0.35s ease',
        }}
      />

      {/* Tooltip card */}
      <div
        style={{
          position: 'absolute',
          top: tipTop,
          left: 16,
          right: 16,
          transform: tipTransform,
          background: '#fff',
          borderRadius: 18,
          padding: 16,
          boxShadow: '0 18px 40px rgba(0,0,0,0.30)',
          animation: 'fadeIn 0.35s ease',
        }}
      >
        <div style={{ position: 'absolute', top: -22, left: 16 }}>
          <Caveat color={P.terraLight} rot={tip.caveatRot} size={20}>
            {tip.caveat}
          </Caveat>
        </div>
        <div style={{ fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 700, color: P.ink, lineHeight: 1.2 }}>
          {tip.title}
        </div>
        <div style={{ fontSize: 13, color: P.muted, marginTop: 6, lineHeight: 1.45 }}>{tip.body}</div>

        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Progress dots */}
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {TIPS.map((_, i) => (
              <button
                key={i}
                aria-label={`Шаг ${i + 1}`}
                onClick={() => setStep(i)}
                style={{
                  width: i === step ? 16 : 6,
                  height: 6,
                  borderRadius: 3,
                  background: i === step ? P.terracotta : 'rgba(45,37,24,0.20)',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  transition: 'all 0.25s ease',
                }}
              />
            ))}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={onDone}
              style={{
                background: 'none',
                border: 'none',
                fontFamily: T.fontBody,
                fontSize: 13,
                color: P.muted,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Пропустить
            </button>
            <button
              onClick={() => (last ? onDone() : setStep(step + 1))}
              style={{
                background: P.ink,
                color: '#fff',
                border: 'none',
                fontFamily: T.fontBody,
                fontSize: 13,
                fontWeight: 600,
                padding: '8px 16px',
                borderRadius: 999,
                cursor: 'pointer',
              }}
            >
              {last ? 'Понятно' : 'Дальше'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
